import React, { useState, useMemo, useCallback, memo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import { FareCard } from './FareCard';
import { PageIndicators } from './PageIndicators';
import { Legend } from './Legend';
export const CARD_WIDTH = 360;
const CARD_GAP = 16;
const DESKTOP_SIDE_PEEK = 140;
const DESKTOP_ACTIVE_CARD_SHIFT = 80;
const MOBILE_CARD_WIDTH = 310;
const DRAG_THRESHOLD = 48;
const INTERACTIVE_SELECTOR = 'a, button, input, textarea, select, [role="button"], [role="link"]';

const isInteractiveElement = (target) => target?.closest?.(INTERACTIVE_SELECTOR);

const FareDialog = ({ open, onClose, data = [], currency }) => {
  const theme = useTheme();
  const colorTheme = useListingColors();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentPage, setCurrentPage] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({
    startX: 0,
    startY: 0,
    deltaX: 0,
    deltaY: 0,
    isPointerDown: false,
    hasDragged: false,
  });
  const suppressClickRef = useRef(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setCurrentPage(0);
    setDragOffset(0);
  }, [open, data.length]);

  const { totalPages, isLastPage, showArrows } = useMemo(() => {
    const total = data.length;

    return {
      totalPages: total,
      isLastPage: currentPage >= total - 1,
      showArrows: total > 1,
    };
  }, [data.length, currentPage]);

  const baseFare = useMemo(() => data[0], [data]);

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : prev));
  }, [totalPages]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleDragStart = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    if (isInteractiveElement(event.target)) {
      return;
    }

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      deltaX: 0,
      deltaY: 0,
      isPointerDown: true,
      hasDragged: false,
    };
    setIsDragging(true);

    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, []);

  const handleDragMove = useCallback(
    (event) => {
      const dragState = dragStateRef.current;

      if (!dragState.isPointerDown) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      const isHorizontalDrag = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 8;
      const isDraggingPastStart = currentPage === 0 && deltaX > 0;
      const isDraggingPastEnd = isLastPage && deltaX < 0;
      const boundedDeltaX = isDraggingPastStart || isDraggingPastEnd ? deltaX * 0.35 : deltaX;

      dragStateRef.current = {
        ...dragState,
        deltaX,
        deltaY,
        hasDragged: dragState.hasDragged || isHorizontalDrag,
      };

      if (isHorizontalDrag) {
        setDragOffset(boundedDeltaX);
      }
    },
    [currentPage, isLastPage]
  );

  const handleDragEnd = useCallback(
    (event) => {
      const dragState = dragStateRef.current;

      if (!dragState.isPointerDown) {
        return;
      }

      const shouldChangePage =
        Math.abs(dragState.deltaX) > DRAG_THRESHOLD &&
        Math.abs(dragState.deltaX) > Math.abs(dragState.deltaY);

      dragStateRef.current = {
        ...dragState,
        isPointerDown: false,
      };
      setIsDragging(false);
      setDragOffset(0);

      event.currentTarget.releasePointerCapture?.(event.pointerId);

      if (shouldChangePage) {
        if (dragState.deltaX < 0) {
          handleNextPage();
        } else {
          handlePrevPage();
        }
      }

      if (dragState.hasDragged) {
        suppressClickRef.current = true;
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
      }
    },
    [handleNextPage, handlePrevPage]
  );

  const handleDragCancel = useCallback((event) => {
    dragStateRef.current = {
      ...dragStateRef.current,
      isPointerDown: false,
    };
    setIsDragging(false);
    setDragOffset(0);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  const handleCarouselClickCapture = useCallback((event) => {
    if (!suppressClickRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const desktopViewportWidth = CARD_WIDTH + CARD_GAP * 2 + DESKTOP_SIDE_PEEK * 2;
  const activeCardWidth = isMobile ? MOBILE_CARD_WIDTH : CARD_WIDTH;
  const carouselViewportWidth = isMobile ? MOBILE_CARD_WIDTH : desktopViewportWidth;
  const centeredTrackOffset = (carouselViewportWidth - activeCardWidth) / 2;
  const activeCardShift = isMobile ? 0 : DESKTOP_ACTIVE_CARD_SHIFT;
  const trackOffset =
    centeredTrackOffset - activeCardShift - currentPage * (activeCardWidth + CARD_GAP) + dragOffset;
  const trackTransform = `translateX(${trackOffset}px)`;

  return (
    <Dialog
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: '100%', sm: '42rem', md: '48rem' },
          height: { xs: '100%', md: 'min(48rem, 90vh)' },
          m: { xs: 0, sm: 2 },
          borderRadius: '15px',
        },
      }}
      fullScreen={isMobile}
      maxWidth={false}
      onClose={onClose}
      open={open}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box component='div'>
          <Typography variant='h6'>Opciones de Tarifas</Typography>
        </Box>
        <IconButton aria-label='close' color='inherit' edge='end' onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          flex: 1,
          minHeight: 0,
        }}
      >
        {showArrows && (
          <>
            <IconButton
              disabled={!currentPage}
              onClick={handlePrevPage}
              sx={{
                display: { sm: 'flex', xs: 'none' },
                position: 'absolute',
                left: { sm: '1rem', md: '1.5rem' },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'background.paper',
                  transform: 'translateY(-50%)',
                },
                '&:focus-visible, &:active': {
                  transform: 'translateY(-50%)',
                },
              }}
            >
              <ChevronLeft />
            </IconButton>

            <IconButton
              disabled={isLastPage}
              onClick={handleNextPage}
              sx={{
                display: { sm: 'flex', xs: 'none' },
                position: 'absolute',
                right: { sm: '1rem', md: '1.5rem' },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'background.paper',
                  transform: 'translateY(-50%)',
                },
                '&:focus-visible, &:active': {
                  transform: 'translateY(-50%)',
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}
        <Box
          sx={{
          py: 2,
          px: { xs: 2, sm: 4, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          mx: 'auto',
          width: '100%',
          maxWidth: { xs: '100%', md: `${desktopViewportWidth}px` },
          flex: 1,
          minHeight: 0,
          '@media (max-height: 780px)': {
            flex: '0 0 auto',
            minHeight: 'auto',
            justifyContent: 'flex-start',
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '@media (max-height: 780px)': {
              flex: '0 0 auto',
              minHeight: 'auto',
              alignItems: 'flex-start',
            },
          }}
        >
            <Box
              onClickCapture={handleCarouselClickCapture}
              onPointerCancel={handleDragCancel}
              onPointerDown={handleDragStart}
              onPointerLeave={handleDragEnd}
              onPointerMove={handleDragMove}
              onPointerUp={handleDragEnd}
              sx={{
                width: { xs: `${MOBILE_CARD_WIDTH}px`, md: `${desktopViewportWidth}px` },
                maxWidth: '100%',
                overflow: 'hidden',
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'pan-y',
                userSelect: isDragging ? 'none' : 'auto',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: `${CARD_GAP}px`,
                  alignItems: 'stretch',
                  transform: trackTransform,
                  transition: isDragging ? 'none' : 'transform 240ms ease',
                  willChange: 'transform',
                }}
              >
                {data.map((fare, index) => (
                  <Box
                    key={fare.id}
                    sx={{
                      flex: {
                        xs: `0 0 ${MOBILE_CARD_WIDTH}px`,
                        md: `0 0 ${CARD_WIDTH}px`,
                      },
                    }}
                  >
                    <FareCard
                      baseFare={baseFare}
                      currency={currency}
                      disabled={index !== currentPage}
                      fare={fare}
                      {...colorTheme}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {showArrows && (
            <PageIndicators
              currentPage={currentPage}
              handleNextPage={handleNextPage}
              handlePrevPage={handlePrevPage}
              onPageChange={handlePageChange}
              totalPages={totalPages}
              {...colorTheme}
            />
          )}
        </Box>
        <Legend />
      </DialogContent>
    </Dialog>
  );
};

export default memo(FareDialog);
